import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { auth, type RoleRef, type UserMe } from "@/api/auth";
import { TokenStore } from "@/api/http";

const WILDCARD = "*";

/**
 * Decoded JWT claims we care about. The bearer-token claim shape comes
 * from `TenantJWTStrategy` in the backend: `sub`, `org_id`, `reg`, `sup`.
 * We avoid pulling in a JWT library on the client — base64-decode the
 * middle segment by hand. (Trust comes from the server validating the
 * signature on every request; client-side claims are display-only.)
 */
interface TokenClaims {
  sub: string;
  org_id: string;
  reg?: string;
  sup?: boolean;
  exp?: number;
}

function decodeJwt(token: string): TokenClaims | null {
  try {
    const [, payload] = token.split(".");
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json) as TokenClaims;
  } catch {
    return null;
  }
}

interface AuthValue {
  user: UserMe | null;
  claims: TokenClaims | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** True when the JWT carries `sup=true`. Drives admin-only nav links. */
  isSuperAdmin: boolean;
  /** Effective permission keys for the caller (from /api/v1/me/permissions). */
  permissions: Set<string>;
  /** Named roles the caller holds. */
  roles: RoleRef[];
  /** True when the caller has the given permission (wildcard `*` passes all). */
  has: (permission: string) => boolean;
  /** True when the caller has at least one of the given permissions. */
  hasAny: (...permissions: string[]) => boolean;
  login: (email: string, password: string, totpCode?: string) => Promise<void>;
  logout: () => void;
  refresh: () => Promise<void>;
}

const Ctx = createContext<AuthValue | null>(null);

export function useAuth(): AuthValue {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth used outside AuthProvider");
  return v;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [token, setToken] = useState<string | null>(() => TokenStore.get());
  const [user, setUser] = useState<UserMe | null>(null);
  const [permissions, setPermissions] = useState<Set<string>>(new Set());
  const [roles, setRoles] = useState<RoleRef[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(!!token);
  const navigate = useNavigate();
  const location = useLocation();

  const claims = useMemo<TokenClaims | null>(() => (token ? decodeJwt(token) : null), [token]);

  const loadAccess = useCallback(async () => {
    try {
      const access = await auth.myPermissions();
      setPermissions(new Set(access.permissions));
      setRoles(access.roles);
    } catch {
      // Non-fatal: a missing/failed permission fetch just means a locked-down
      // menu, not a broken session. The server still enforces every route.
      setPermissions(new Set());
      setRoles([]);
    }
  }, []);

  const refresh = useCallback(async () => {
    if (!TokenStore.get()) {
      setUser(null);
      setPermissions(new Set());
      setRoles([]);
      setIsLoading(false);
      return;
    }
    try {
      const me = await auth.me();
      setUser(me);
      await loadAccess();
    } catch {
      // 401 already cleared the token via http.ts; the auth-expired
      // event listener (below) will route us to /login.
      setUser(null);
      setPermissions(new Set());
      setRoles([]);
    } finally {
      setIsLoading(false);
    }
  }, [loadAccess]);

  // Initial bootstrap.
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Global handler for token expiration (dispatched by http.ts on 401).
  useEffect(() => {
    function onExpired() {
      setToken(null);
      setUser(null);
      setPermissions(new Set());
      setRoles([]);
      // Preserve where the user was so we can return them after login.
      navigate("/login", { state: { returnTo: location.pathname }, replace: true });
    }
    window.addEventListener("piggy:auth-expired", onExpired);
    return () => window.removeEventListener("piggy:auth-expired", onExpired);
  }, [navigate, location.pathname]);

  const login: AuthValue["login"] = async (email, password, totpCode) => {
    const res = await auth.login(email, password, totpCode);
    TokenStore.set(res.access_token);
    setToken(res.access_token);
    const me = await auth.me();
    setUser(me);
    await loadAccess();
  };

  const logout = useCallback(() => {
    TokenStore.clear();
    setToken(null);
    setUser(null);
    setPermissions(new Set());
    setRoles([]);
    navigate("/login", { replace: true });
  }, [navigate]);

  const has = useCallback(
    (permission: string) => permissions.has(WILDCARD) || permissions.has(permission),
    [permissions],
  );
  const hasAny = useCallback(
    (...perms: string[]) => permissions.has(WILDCARD) || perms.some((p) => permissions.has(p)),
    [permissions],
  );

  const value: AuthValue = {
    user,
    claims,
    isAuthenticated: !!user,
    isLoading,
    isSuperAdmin: !!claims?.sup,
    permissions,
    roles,
    has,
    hasAny,
    login,
    logout,
    refresh,
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
